import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { BAND_STYLE } from '@/components/health/bandStyle';
import { IndicatorBadge } from '@/components/health/IndicatorBadge';
import { indicatorUnit } from '@/components/health/indicatorUnit';
import { bandOf, type NamatAssessment } from '@/lib/assessment/namatAssessment';
import { loadMyNamatScale, type MyNamatScale } from '@/lib/firebase/queries/namatAssessment';
import { getAssignedQuestionnaire } from '@/lib/firebase/queries/questionnaireRules';
import { cn } from '@/lib/utils';
import { wantsScaleOpen, withoutScaleParam } from '@/lib/questionnaireLink/questionnaireLink';
import { NamatScaleQuestionnaire } from './NamatScaleQuestionnaire';
import { OPEN_NAMAT_SCALE_EVENT, SCORE_UPDATE_EVENT } from './score-events';

// «مقياس نمط» (#124): the window with the beneficiary's result, the exams that
// count in it and the result per pillar, or the way into the questionnaire.
// Mounted on the beneficiary's own dashboard for anyone with an active customer
// role (every account is a beneficiary) and opened from the avatar
// (OPEN_NAMAT_SCALE_EVENT); it has no button of its own (the top-bar icon and
// its status dot were removed, owner request 2026-10-07). Opens by itself once
// per browser session while the questionnaire is not valid.

type Status = NamatAssessment['status'];

const STATUS_BADGE: Record<Status, string> = {
  not_done: 'bg-orange-100 text-orange-800',
  valid: 'bg-green-100 text-green-800',
  expired: 'bg-blue-100 text-blue-800',
};

const AUTO_OPEN_KEY = 'namatScaleAutoOpened_';
/** Fallback when sessionStorage is blocked: once per page load. */
const autoOpenedInMemory = new Set<string>();

/** True the first time in this browser session for this user. */
function firstTimeThisSession(uid: string): boolean {
  if (autoOpenedInMemory.has(uid)) return false;
  autoOpenedInMemory.add(uid);
  try {
    if (sessionStorage.getItem(AUTO_OPEN_KEY + uid)) return false;
    sessionStorage.setItem(AUTO_OPEN_KEY + uid, '1');
  } catch {
    // Storage blocked (private mode): the in-memory flag alone applies.
  }
  return true;
}

/**
 * The layout remounts on every route, so the last read is kept per user and
 * reused until the questionnaire is saved.
 */
let cached: { uid: string; promise: Promise<MyNamatScale> } | null = null;

function readScale(uid: string, fresh = false): Promise<MyNamatScale> {
  if (fresh || !cached || cached.uid !== uid) {
    const promise = loadMyNamatScale(uid);
    cached = { uid, promise };
    promise.catch(() => {
      if (cached?.promise === promise) cached = null;
    });
  }
  return cached.promise;
}

export function NamatScaleButton() {
  const { user, hasRole } = useAuth();
  const { t, language, dir } = useLanguage();
  const uid = user && hasRole('customer') ? user.uid : null;
  const location = useLocation();
  const navigate = useNavigate();

  const [scale, setScale] = useState<MyNamatScale | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [filling, setFilling] = useState(false);
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);

  const refresh = useCallback(
    async (fresh = false) => {
      if (!uid) return;
      try {
        const next = await readScale(uid, fresh);
        setScale(next);
        setFailed(false);
      } catch (error) {
        console.error('Error loading the Namat scale:', error);
        setFailed(true);
      }
    },
    [uid]
  );

  useEffect(() => {
    if (!uid) return;
    refresh();
    // A questionnaire saved anywhere: re-read once the write has landed.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onScoreUpdate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => refresh(true), 1000);
    };
    window.addEventListener(SCORE_UPDATE_EVENT, onScoreUpdate);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(SCORE_UPDATE_EVENT, onScoreUpdate);
    };
  }, [uid, refresh]);

  const status = scale?.assessment.status ?? null;

  // The avatar on the beneficiary dashboard opens the same window.
  useEffect(() => {
    if (!uid) return;
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_NAMAT_SCALE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_NAMAT_SCALE_EVENT, onOpen);
  }, [uid]);

  // The public questionnaire link `/q` (#125) lands here with ?scale=open.
  useEffect(() => {
    if (!uid || !wantsScaleOpen(location.search)) return;
    setOpen(true);
    navigate({ pathname: location.pathname, search: withoutScaleParam(location.search) }, { replace: true });
  }, [uid, location.pathname, location.search, navigate]);

  // Not filled or expired: open by itself once per browser session.
  useEffect(() => {
    if (!uid || !status || status === 'valid') return;
    if (firstTimeThisSession(uid)) setOpen(true);
  }, [uid, status]);

  // How many questions await (not done only), read when the window opens.
  useEffect(() => {
    if (!uid || !open || status !== 'not_done' || questionCount !== null) return;
    let cancelled = false;
    getAssignedQuestionnaire(uid)
      .then((q) => !cancelled && setQuestionCount(q?.questions?.length ?? 0))
      .catch(() => !cancelled && setQuestionCount(0));
    return () => {
      cancelled = true;
    };
  }, [uid, open, status, questionCount]);

  // No page scroll behind the questionnaire.
  useEffect(() => {
    if (!filling) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [filling]);

  if (!uid) return null;

  const assessment = scale?.assessment;
  const formatDate = (d: Date) => d.toLocaleDateString(language === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-GB');
  const bilingual = (text?: { en: string; ar: string }) => (text ? (language === 'ar' ? text.ar : text.en) : '—');

  const startFilling = () => {
    setOpen(false);
    setFilling(true);
  };

  const onQuestionnaireComplete = async () => {
    setFilling(false);
    setShowAnswers(false);
    setShowPrevious(false);
    await refresh(true);
    setOpen(true);
  };

  const pillarBars = (muted: boolean) =>
    scale && scale.pillars.length > 0 ? (
      <div className={cn('space-y-3', muted && 'opacity-60')} data-testid="scale-pillars">
        {scale.pillars.map((p) => (
          <div key={p.pillar} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span>{t(`habits.categories.${p.pillar}`)}</span>
              <span className="font-medium tabular-nums">{p.percent}%</span>
            </div>
            <Progress value={p.percent} className={cn('h-2', muted && 'grayscale')} />
          </div>
        ))}
      </div>
    ) : null;

  const answersList = (muted: boolean) =>
    scale && scale.answersByPillar.length > 0 ? (
      <div className={cn('space-y-2', muted && 'opacity-60')}>
        <button
          type="button"
          onClick={() => setShowAnswers((v) => !v)}
          className="flex items-center gap-1 text-sm font-medium text-[#004250] hover:underline"
          aria-expanded={showAnswers}
          data-testid="scale-answers-toggle"
        >
          {t('common.namatScale.myAnswers')}
          {showAnswers ? (
            <ChevronDown className="h-4 w-4" />
          ) : dir === 'rtl' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {showAnswers && (
          <div className="space-y-4 rounded-md border p-3" data-testid="scale-answers">
            {scale.answersByPillar.map((group) => (
              <div key={group.pillar} className="space-y-2">
                <h4 className="text-sm font-semibold">{t(`habits.categories.${group.pillar}`)}</h4>
                <ul className="space-y-2">
                  {group.answers.map((a) => (
                    <li key={a.questionId} className="text-sm">
                      <p>{bilingual(a.questionText)}</p>
                      <p className="text-muted-foreground">{bilingual(a.optionText)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

  // «الفحوصات»: the exam the scale used, each outcome's
  // value, level and points, and how the total splits (scaleBreakdown). Greyed
  // under an expired (previous) result, like its percentage and pillars.
  const examsSection = (muted: boolean) => {
    const b = scale?.breakdown;
    if (!b) return null;
    return (
      <div
        className={cn('space-y-2 rounded-md border p-3', muted && 'opacity-60 grayscale')}
        data-testid="scale-exams"
        data-muted={muted ? 'true' : undefined}
      >
        <h4 className="text-sm font-semibold">{t('common.namatScale.exams.title')}</h4>
        {b.exam ? (
          <>
            <p className="text-xs text-muted-foreground" data-testid="scale-exam-date">
              {t('common.namatScale.exams.date', { date: formatDate(b.exam.date) })}
            </p>
            <ul className="divide-y">
              {b.outcomes.map((o) => (
                <li key={o.outcome} className="flex items-center justify-between gap-2 py-2" data-testid={`scale-exam-${o.outcome}`}>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{t(`common.health.indicators.${o.outcome}`)}</p>
                    {o.reading && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm tabular-nums" dir="ltr">
                          {o.reading.value} {indicatorUnit(o.reading, t)}
                        </span>
                        <IndicatorBadge reading={o.reading} t={t} />
                      </div>
                    )}
                  </div>
                  <span
                    className={cn('shrink-0 text-sm', o.state === 'scored' ? 'font-semibold tabular-nums' : 'text-muted-foreground')}
                  >
                    {o.state === 'scored'
                      ? t('common.namatScale.exams.points', { points: o.points ?? 0, max: o.max })
                      : t(`common.namatScale.exams.state.${o.state}`)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="scale-no-exam">
            {t('common.namatScale.exams.none')}
          </p>
        )}
        <div className="space-y-0.5 border-t pt-2 text-sm" data-testid="scale-split">
          <p>{t('common.namatScale.exams.questionnairePart', { score: b.questionnaire.score, max: b.questionnaire.max })}</p>
          {b.examPart.max > 0 && <p>{t('common.namatScale.exams.examPart', { score: b.examPart.score, max: b.examPart.max })}</p>}
          <p className="font-semibold">
            {t('common.namatScale.exams.total', { score: b.total.score, max: b.total.max, percent: b.total.percent })}
          </p>
        </div>
      </div>
    );
  };

  const primaryClass = 'bg-[#7dda55] hover:bg-[#6dc447] text-white';

  let body: React.ReactNode;
  if (!assessment) {
    body = (
      <p className="text-sm text-muted-foreground">
        {failed ? t('common.namatScale.loadError') : t('common.namatScale.loading')}
      </p>
    );
  } else if (assessment.status === 'not_done') {
    body = (
      <div className="space-y-4" data-testid="scale-not-done">
        <p className="text-sm leading-relaxed">{t('common.namatScale.intro')}</p>
        {questionCount !== null && questionCount > 0 && (
          <p className="text-sm text-muted-foreground">{t('common.namatScale.questionCount', { count: questionCount })}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={startFilling} className={primaryClass}>
            {t('common.namatScale.start')}
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('common.namatScale.later')}
          </Button>
        </div>
      </div>
    );
  } else if (assessment.status === 'valid') {
    const percent = assessment.scale?.percent ?? 0;
    const band = bandOf(percent);
    body = (
      <div className="space-y-5" data-testid="scale-valid">
        <div className="flex items-center gap-3">
          <span className="text-4xl font-bold tabular-nums">{percent}%</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${BAND_STYLE[band]}`}>
            {t(`common.health.scale.band.${band}`)}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          {assessment.expiresAt && <span>{t('common.namatScale.validUntil', { date: formatDate(assessment.expiresAt) })}</span>}
          {scale?.previousMonth && (
            <span data-testid="scale-last-month">{t('common.namatScale.lastMonth', { percent: scale.previousMonth.percent })}</span>
          )}
        </div>
        {examsSection(false)}
        {pillarBars(false)}
        {answersList(false)}
        {assessment.expiresAt && (
          <p className="text-xs text-muted-foreground border-t pt-3">
            {t('common.namatScale.refillFrom', { date: formatDate(assessment.expiresAt) })}
          </p>
        )}
      </div>
    );
  } else {
    const percent = assessment.scale?.percent ?? null;
    body = (
      <div className="space-y-4" data-testid="scale-expired">
        <div className="space-y-1 text-muted-foreground">
          <span className="block text-4xl font-bold tabular-nums opacity-60">{percent === null ? '—' : `${percent}%`}</span>
          {assessment.filledAt && (
            <span className="block text-sm">
              {t('common.health.scale.lastResult', { percent: percent ?? '—', date: formatDate(assessment.filledAt) })}
            </span>
          )}
        </div>
        {examsSection(true)}
        {showPrevious && (
          <div className="space-y-4">
            {pillarBars(true)}
            {answersList(true)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={startFilling} className={primaryClass}>
            {t('common.namatScale.update')}
          </Button>
          {!showPrevious && scale && scale.pillars.length > 0 && (
            <Button variant="outline" onClick={() => setShowPrevious(true)}>
              {t('common.namatScale.showPrevious')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t('common.namatScale.later')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir={dir} className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader className={dir === 'rtl' ? 'text-right sm:text-right' : undefined}>
            <div className="flex flex-wrap items-center gap-2">
              <DialogTitle>{t('common.namatScale.title')}</DialogTitle>
              {status && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status]}`} data-testid="scale-status">
                  {t(`common.namatScale.status.${status}`)}
                </span>
              )}
            </div>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>

      {/* Portaled: the header's backdrop blur would otherwise contain the fixed overlay. */}
      {filling &&
        createPortal(
          <NamatScaleQuestionnaire
            onComplete={onQuestionnaireComplete}
            onClose={() => setFilling(false)}
            expired={
              assessment?.status === 'expired' && assessment.filledAt
                ? { percent: assessment.scale?.percent ?? null, filledAt: assessment.filledAt }
                : undefined
            }
          />,
          document.body
        )}
    </>
  );
}
